import { Box } from "@mui/material"
import { useState } from "react"
import WorkLists from "../../components/WorkLists/WorkLists"
import ValidationRules from "../../components/ValidationRules/ValidationRules"
import ManageTeams from "../../components/ManageTeams/ManageTeams"
import { ValidationOutcome } from "../../api/generated/models/ValidationOutcome"
import {
  useUpdateValidationRule,
  useValidationRules,
  useOpcoValidationConfigRules
} from "../../hooks/ValidationRules"
import {
  useCreateWorkList,
  useDeleteWorkList,
  useUpdateWorkList,
  useWorkLists,
} from "../../hooks/WorkLists"
import { useGetUsers, useGetAllUsersByTeamsAndRole } from "../../hooks/Users"
import {
  useCreateTeam,
  useDeleteTeam,
  useGetTeams,
  useUpdateTeam,
} from "../../hooks/Teams"
import { UserRole } from "../../api/generated"
import useShowError from "../../hooks/useShowError"
import useShowToast from "../../hooks/useShowToast"
import OpCoTable from "../../components/OpCoTable/OpCoTable"
import RejEmailTemplate from '../../components/RejectionEmailTemplate/RejectionEmailTemplate'
import { useGetAllOpco } from "../../hooks/OpCo"
import ValidationConfig from '../../components/ValidationConfig/ValidationConfig'
import ContactForm from "../../components/ContactForm/ContactForm"
import UserManagementTable from "../../components/UserManagement/Usermanagement"
import SecondaryTabsNav from "../../components/SecondaryTabsNav/SecondaryTabsNav";
import { useCurrentUser } from "../../hooks/Users"

export default function Settings() {
  const showError = useShowError();
  const showToast = useShowToast();

  const validationRules = useValidationRules()
  const opcoValidationConfigRules = useOpcoValidationConfigRules()
  const updateValidationRule = useUpdateValidationRule()

  const workLists = useWorkLists()
  const createWorkListMutation = useCreateWorkList()
  const updateWorkListMutation = useUpdateWorkList()
  const deleteWorkListMutation = useDeleteWorkList()

  const users = useGetUsers()
  const filteredUsers = useGetAllUsersByTeamsAndRole()
  const currentUser = useCurrentUser();

  const teamLeads = users.data!.filter(
    (user) => user.role?.toLowerCase() === UserRole.TEAM_LEAD,
  )

  const teams = useGetTeams()
  const createTeamMutation = useCreateTeam()
  const updateTeamMutation = useUpdateTeam()
  const deleteTeamMutation = useDeleteTeam()
  const allOpcosQuery = useGetAllOpco()
  const [selectedTab, setSelectedTab] = useState<number>(0);

  const tabLabels = [
    "Contact info",
    "Business unit",
    "Validation rules selection",
    "Validation rules configuration",
    "Rejection email",
    "Worklists",
    "Teams",
    "User management"
  ];

  const tabContents = [
    <Box><ContactForm /> </Box>,
    <Box>
      <OpCoTable
        allOpcosQuery={allOpcosQuery}
      />
    </Box>,
    <Box>
      <ValidationRules
        onChangeOutcome={(validationRule, outcome) => {
          updateValidationRule.mutate({
            ruleId: validationRule.id,
            outcome,
          }, {
            onSuccess: () => {
              showToast("Action has been successfully updated.")
            },
            onError: () => {
              showError("Something went wrong, please try again.")
            },
          }
          )
        }}
        onChangeStatus={(validationRule, status) => {
          updateValidationRule.mutate({
            ruleId: validationRule?.id,
            outcome: validationRule?.outcome as ValidationOutcome,
            status
          }, {
            onSuccess: () => {
              showToast("Status has been successfully updated.")
            },
            onError: () => {
              showError("Something went wrong, please try again.")
            },
          })
        }}
        validationRules={validationRules.data!}
      />
    </Box>,
    <Box>
      <ValidationConfig
        opcosList={opcoValidationConfigRules.data!}
      />
    </Box>,
    <Box>
      <RejEmailTemplate
        opcosList={allOpcosQuery.data!}
      />
    </Box>,
    <Box>
      <WorkLists
        onUpdate={updateWorkListMutation.mutate}
        onDelete={deleteWorkListMutation.mutate}
        workListItems={workLists.data!}
        opcosList={allOpcosQuery.data!}
      />
    </Box>,
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      gap: "32px",
    }}>
      <ManageTeams
        onCreate={createTeamMutation.mutate}
        onUpdate={updateTeamMutation.mutate}
        onDelete={deleteTeamMutation.mutate}
        teamLeads={teamLeads}
        workLists={workLists.data!}
        teams={teams.data!}
      />
    </Box>,
    <Box>
      <UserManagementTable
        users={currentUser?.data?.role?.name?.toLowerCase() === UserRole.TEAM_LEAD || currentUser?.data?.role?.name?.toLowerCase() === UserRole.USER ? filteredUsers : users}
        teams={teams.data!}
        userRole={currentUser?.data?.role?.name}
      />
    </Box>,
  ];

  const tabsConfig = tabLabels.map((label, index) => ({
    label,
    content: tabContents[index],
    visibleTo: index === 7 ? [UserRole.TEAM_LEAD, UserRole.SYSTEM_ADMIN, UserRole.CLIENT_ADMIN,] : [UserRole.SYSTEM_ADMIN, UserRole.CLIENT_ADMIN,]
  }));

  const filteredTabs = tabsConfig.filter(tab => tab.visibleTo.includes(currentUser?.data?.role?.name?.toLowerCase() as UserRole));

  const filteredTabLabels = filteredTabs.map(tab => tab.label);

  return (
    <Box>
      <SecondaryTabsNav
        tabs={filteredTabLabels}
        value={selectedTab}
        onChange={(_, newValue) => setSelectedTab(newValue)}
      />

      <Box sx={{ padding: "32px", background: 'var(--background-off-white)' }}>
        {filteredTabs[selectedTab].content}
      </Box>
    </Box>
  )
}
